import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators'; // still used by getExampleQuestions
import { environment } from '../../environments/environment';
import { ExampleQuestion, ExampleResponse, NlBiAskResponse, NlBiFeedbackResponse } from '../models/chatbot.model';

@Injectable({
  providedIn: 'root',
})
export class ChatbotService {
  private readonly baseUrl = environment.NL_BI_AGENT_API_URL || 'http://localhost:8000';

  private exampleQuestions$: BehaviorSubject<ExampleQuestion[]> = new BehaviorSubject<ExampleQuestion[]>([]);

  constructor(private http: HttpClient) {}

  /**
   * Send a question to the NL-BI-Agent API.
   * Pass `conversationId` only when the caller has determined this is a follow-up
   * question — omitting it causes the backend to start a fresh context window.
   */
  public sendMessage(question: string, userId?: string | null, conversationId?: string | null): Observable<NlBiAskResponse> {
    const body: Record<string, string> = { question };
    if (conversationId) {
      body['conversation_id'] = conversationId;
    }
    if (userId) {
      body['user_id'] = userId;
    }
    return this.http.post<NlBiAskResponse>(`${this.baseUrl}/api/v1/ask`, body);
  }

  /**
   * Submit a 1–5 star rating (with optional comment) for a specific turn.
   */
  public submitFeedback(turnId: string, rating: number, comment?: string): Observable<NlBiFeedbackResponse> {
    const body: Record<string, any> = { rating };
    if (comment) {
      body['comment'] = comment;
    }
    return this.http.post<NlBiFeedbackResponse>(`${this.baseUrl}/api/v1/turns/${turnId}/feedback`, body);
  }

  /**
   * Get example questions organised by category (hardcoded — the NL-BI-Agent
   * API does not expose an /examples endpoint).
   */
  public getExampleCategories(): Observable<ExampleResponse> {
    return of({
      examples: [
        {
          category: 'Cost Analysis',
          questions: [
            'What are the top 10 shops by total repair cost this year?',
            'Show me the monthly repair cost trend for 2025',
            'Which countries have the highest average repair cost per work order?',
            'Show total cost by shop for the last 6 months',
          ],
        },
        {
          category: 'Work Orders',
          questions: [
            'How many work orders are pending approval?',
            'Show me work order counts by status',
            'What is the rejection rate by shop?',
            'How many work orders were created last month?',
          ],
        },
        {
          category: 'Approvals',
          questions: [
            'What is the auto vs manual approval percentage?',
            'Which shops have the longest approval turnaround time?',
            'Show approval trends by month',
            'What are the rule engine statistics for the last quarter?',
          ],
        },
        {
          category: 'Damage Codes',
          questions: ['Show me the repair code distribution for the last 3 months', 'Which repair codes are used most frequently?'],
        },
      ],
    });
  }

  /**
   * Get a flat shuffled list of example questions (2 per category).
   */
  public getExampleQuestions(): Observable<ExampleQuestion[]> {
    if (this.exampleQuestions$.value.length === 0) {
      return this.getExampleCategories().pipe(
        tap((response) => {
          const questions: ExampleQuestion[] = [];
          response.examples.forEach((category) => {
            const shuffled = [...category.questions].sort(() => Math.random() - 0.5);
            shuffled.slice(0, 2).forEach((q) => {
              questions.push({
                id:
                  q
                    .toLowerCase()
                    .replaceAll(/[^a-z0-9]/g, '')
                    .substring(0, 20) + Date.now().toString(36),
                question: q,
                category: category.category,
              });
            });
          });
          this.exampleQuestions$.next([...questions].sort(() => Math.random() - 0.5));
        }),
        // Return the subject value after populating it
        // (tap runs synchronously for `of(...)`)
      ) as any;
    }
    return this.exampleQuestions$.asObservable();
  }
}
